import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Switch } from '@/components/ui/switch';
import { execute } from '@/graphql/execute';
import { createProjectLinkMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFeatureCheck } from '@/hooks/useFeature';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { analytics } from '@/lib/analytics';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

const createLinkSchema = z
  .object({
    expiresAt: z.string().min(1, 'Expiration date is required'),
    name: z.string().min(1, 'Name is required'),
    password: z.string().optional(),
    allowQuickUploads: z.boolean().optional(),
  })
  .refine(
    (data) => {
      const expirationDate = dayjs(data.expiresAt);
      const now = dayjs();
      const maxDate = now.add(3, 'months');

      if (expirationDate.isBefore(now)) {
        return false;
      }
      if (expirationDate.isAfter(maxDate)) {
        return false;
      }
      return true;
    },
    {
      message: 'Expiration date must be between now and 3 months from now',
      path: ['expiresAt'],
    },
  );

type CreateLinkFormData = z.infer<typeof createLinkSchema>;

interface ProjectLinkCreateDialogProps {
  projectId: string;
}

export function ProjectLinkCreateDialog({ projectId }: ProjectLinkCreateDialogProps) {
  const { t } = useTranslation('components');
  const { dismissActiveDialog } = useDialog();
  const checkFeature = useFeatureCheck();
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateLinkFormData>({
    resolver: zodResolver(createLinkSchema),
    defaultValues: {
      expiresAt: dayjs().add(1, 'month').format('YYYY-MM-DD'),
      name: t('linkCreate.untitled'),
      password: '',
      allowQuickUploads: false,
    },
  });

  // Create project link mutation
  const createLinkMutation = useMutation({
    mutationFn: async (data: CreateLinkFormData) => {
      return execute(createProjectLinkMutation, {
        projectId,
        expiresAt: dayjs(data.expiresAt).toDate(),
        name: data.name,
        password: data.password || null,
        allowQuickUploads: data.allowQuickUploads ?? false,
      });
    },
    onSuccess: () => {
      analytics.capture('Project Link Created');
      toast.success(t('linkCreate.created'));
      form.reset();
      dismissActiveDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || t('linkCreate.failedCreate'));
    },
  });

  const handleCreateLink = async (data: CreateLinkFormData) => {
    const gate = checkFeature('sharing.publicLink');
    if (!gate.allowed) {
      dismissActiveDialog();
      gate.promptUpgrade();
      return;
    }
    setIsCreating(true);
    try {
      await createLinkMutation.mutateAsync(data);
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allProjectLinks'] });
    } finally {
      setIsCreating(false);
    }
  };

  const getMinDate = () => {
    return dayjs().format('YYYY-MM-DD');
  };

  const getMaxDate = () => {
    return dayjs().add(3, 'months').format('YYYY-MM-DD');
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('linkCreate.title')}</DialogTitle>
        <DialogDescription>
          {t('linkCreate.description')}
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleCreateLink)} className='space-y-6'>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('linkCreate.name')}</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isCreating} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='expiresAt'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('linkCreate.expirationDate')}</FormLabel>
                <FormControl>
                  <Input type='date' min={getMinDate()} max={getMaxDate()} {...field} disabled={isCreating} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('linkCreate.passwordOptional')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('linkCreate.passwordPlaceholder')}
                    showLabel={t('linkCreate.showPassword')}
                    hideLabel={t('linkCreate.hidePassword')}
                    {...field}
                    disabled={isCreating}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='allowQuickUploads'
            render={({ field }) => (
              <FormItem className='flex items-start justify-between gap-4 rounded-md border p-3'>
                <div className='space-y-1'>
                  <FormLabel>{t('linkCreate.allowQuickUploadsLabel')}</FormLabel>
                  <p className='text-xs text-muted-foreground'>
                    {t('linkCreate.allowQuickUploadsHelp')}
                  </p>
                </div>
                <FormControl>
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} disabled={isCreating} />
                </FormControl>
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button type='button' variant='outline' onClick={dismissActiveDialog} disabled={isCreating}>
              {t('linkCreate.cancel')}
            </Button>
            <Button type='submit' disabled={isCreating}>
              {isCreating ? t('linkCreate.creating') : t('linkCreate.createLink')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
